import dayjs from 'dayjs';
import { HeartIcon, PencilIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { apiClient } from '@app/apiClient';
import { useCommonNavigate } from '@client/component/CommonLink';
import { CommonTimeFormat } from '@client/component/CommonDateFormat';
import Jsonld from '../Jsonld';
import MetaHeader from '@client/component/MetaHeader';
import StrategyReturnHero from './StrategyReturnHero';
import firebaseStore from '@client/stores/firebaseStore';
import {
    StrategyResponse,
    StrategyNavDailyResponse,
    StrategyWeightResponse,
} from '../../../response/responseSchema';
import StrategyPerformanceChartBox from './components/StrategyPerformanceChartBox';
import StrategyHoldingsBox from './components/StrategyHoldingsBox';
import StrategyStockReturnsBox from './components/StrategyStockReturnsBox';
import StrategyRebalanceHistoryBox from './components/StrategyRebalanceHistoryBox';
import { MarkdownViewer } from '@client/component/editor/MarkdownViewer';
import { StrategyInfoGuideBox } from './components/StrategyInfoSection';

class ViewStore {
    // API 응답 데이터
    strategy: StrategyResponse | null = null;
    navHistory: StrategyNavDailyResponse[] = [];
    weightHistory: StrategyWeightResponse[] = [];

    isLoading = true; // 초기 로딩 상태

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(hash: string) {
        this.isLoading = true;

        try {
            // 1. 전략 기본 정보 조회 (public API)
            const strategy = await apiClient.strategy.public.getStrategy.$query({ hash });

            // 2. 로그인 여부 확인
            const isLoggedIn = await firebaseStore.isLoggedIn();

            // 3. NAV 히스토리 조회 (로그인 필요, 수익률 Hero 표시용)
            if (isLoggedIn) {
                try {
                    const [navHistory, weightHistory] = await Promise.all([
                        apiClient.strategy.member.getNavHistory.$query({ hash }),
                        apiClient.strategy.member.getWeightHistory.$query({ hash }),
                    ]);

                    runInAction(() => {
                        this.navHistory = navHistory;
                        this.weightHistory = weightHistory;
                    });
                } catch (error) {
                    console.error('Failed to fetch NAV/weight history:', error);
                    // 로그인했지만 본인 전략이 아니면 에러 발생 가능 - 무시
                }
            }

            runInAction(() => {
                this.strategy = strategy;
                this.isLoading = false;
            });
        } catch (error) {
            console.error('Failed to fetch strategy:', error);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    // Computed: 본인 전략 여부
    get isMyStrategy() {
        return this.strategy && this.strategy.user.hash === firebaseStore.me?.hash;
    }

    // Computed: 총 수익률
    get totalReturn() {
        const latestNav = this.navHistory[this.navHistory.length - 1];
        return latestNav ? (latestNav.nav - 100) / 100 : 0;
    }

    // Computed: 일간 수익률
    get dailyReturn() {
        const latestNav = this.navHistory[this.navHistory.length - 1];
        return latestNav ? latestNav.dailyReturn : 0;
    }

    // Computed: 월간 수익률 (TODO: 실제 계산 필요)
    get monthlyReturn() {
        return this.totalReturn;
    }

    // Computed: 연간 수익률 (TODO: 실제 계산 필요)
    get yearlyReturn() {
        return this.totalReturn;
    }
}

const StrategyDetailPage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();
    const { hash } = useParams<{ hash: string }>();

    useEffect(() => {
        if (hash) {
            viewStore.fetch(hash);
        }
    }, [viewStore, hash]);

    if (viewStore.isLoading) {
        return (
            <Frame>
                <Box isLoading={true} />
                <Box isLoading={true} />
                <Box isLoading={true} />
                <Frame.Spacer />
            </Frame>
        );
    }

    if (!viewStore.strategy) {
        return (
            <Frame>
                <Box>
                    <div className="text-base-content/80 flex items-center justify-center py-8">
                        전략을 찾을 수 없습니다
                    </div>
                </Box>
                <Frame.Spacer />
            </Frame>
        );
    }

    const title = viewStore.strategy.name;
    const description = viewStore.strategy.description || '';

    // 일수 계산
    const daysSinceStart = dayjs().diff(dayjs(viewStore.strategy.startDate), 'day');

    return (
        <Frame className="pt-0">
            <MetaHeader title={title} description={description} />
            <Jsonld type="Blog" title={title} description={description} />

            <Frame.Title title={title} isShowOnlyHeader={true} />

            {/* 전략 기본 정보 */}
            <Box>
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold">{viewStore.strategy.name}</h2>

                        {/* 작성자 + 날짜 메타 정보 */}
                        <div className="mt-1 text-base">
                            <span className="text-base-content font-medium">
                                {viewStore.strategy.user?.name}
                            </span>
                            <span className="text-base-content/50 text-sm">
                                {' '}
                                •{' '}
                                {viewStore.strategy.rebalanceDate ? (
                                    <>
                                        <CommonTimeFormat
                                            time={viewStore.strategy.rebalanceDate}
                                            fromNow={true}
                                            isShowHour={false}
                                        />{' '}
                                        업데이트
                                    </>
                                ) : (
                                    <>
                                        <CommonTimeFormat
                                            time={viewStore.strategy.startDate}
                                            isShowHour={false}
                                            fromNow={true}
                                        />{' '}
                                        생성
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                    {viewStore.isMyStrategy ? (
                        <button
                            className="btn btn-ghost btn-xs gap-1"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/strategy/${viewStore.strategy!.hash}/edit`);
                            }}
                        >
                            <PencilIcon className="size-3" />
                            <span className="text-xs">수정</span>
                        </button>
                    ) : (
                        <button
                            className="btn btn-ghost btn-xs gap-1"
                            onClick={(e) => {
                                e.preventDefault();
                                // TODO: 팔로우 토글 API 호출
                            }}
                        >
                            <HeartIcon className="size-3" />
                            <span className="text-xs">팔로우</span>
                        </button>
                    )}
                </div>

                {viewStore.strategy.description && (
                    <div className="text-base-content/80 mt-4 text-base">
                        <MarkdownViewer markdown={viewStore.strategy.description} />
                    </div>
                )}
            </Box>

            {/* 전략 정보 입력 가이드 (이름/설명이 비어있을 때만 표시) */}
            {viewStore.isMyStrategy &&
                (!viewStore.strategy.name || !viewStore.strategy.description) && (
                    <StrategyInfoGuideBox
                        strategyHash={viewStore.strategy.hash}
                        strategyName={viewStore.strategy.name || ''}
                        description={viewStore.strategy.description || ''}
                        onSaved={() => {
                            // 저장 후 데이터 새로고침
                            viewStore.fetch(viewStore.strategy!.hash);
                        }}
                    />
                )}

            {/* 수익률 정보 */}
            <Box>
                <StrategyReturnHero
                    totalReturn={viewStore.totalReturn}
                    dailyReturn={viewStore.dailyReturn}
                    monthlyReturn={viewStore.monthlyReturn}
                    yearlyReturn={viewStore.yearlyReturn}
                    daysSinceStart={daysSinceStart}
                />
            </Box>

            {/* 수익률 추이 차트 TODO: 추가 필요*/}
            {/* <StrategyPerformanceChartBox strategyHash={hash!} /> */}

            {/* 종목 구성 */}
            <StrategyHoldingsBox strategyHash={hash!} />

            {/* 종목별 성과 */}
            <StrategyStockReturnsBox strategyHash={hash!} />

            {/* 변경 기록 */}
            <StrategyRebalanceHistoryBox strategyHash={hash!} />

            <Frame.BottomSpacer />
        </Frame>
    );
});

export default StrategyDetailPage;
